import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Sparkles, 
  Calendar, 
  Clock, 
  MapPin, 
  User,
  Save,
  Download,
  Share2,
  Loader2,
  AlertCircle,
  CheckCircle,
  Bot,
  Wand2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Planet data for display
const PLANETS = [
  { id: 'sun', name: 'Sun', nameHi: 'सूर्य', symbol: '☉', color: '#FF6B6B' },
  { id: 'moon', name: 'Moon', nameHi: 'चंद्र', symbol: '☽', color: '#F0F0F0' },
  { id: 'mars', name: 'Mars', nameHi: 'मंगल', symbol: '♂', color: '#E74C3C' },
  { id: 'mercury', name: 'Mercury', nameHi: 'बुध', symbol: '☿', color: '#2ECC71' },
  { id: 'jupiter', name: 'Jupiter', nameHi: 'गुरु', symbol: '♃', color: '#F39C12' },
  { id: 'venus', name: 'Venus', nameHi: 'शुक्र', symbol: '♀', color: '#9B59B6' },
  { id: 'saturn', name: 'Saturn', nameHi: 'शनि', symbol: '♄', color: '#34495E' },
  { id: 'rahu', name: 'Rahu', nameHi: 'राहु', symbol: '☊', color: '#1ABC9C' },
  { id: 'ketu', name: 'Ketu', nameHi: 'केतु', symbol: '☋', color: '#E67E22' }
];

const RASHIS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const HOUSES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export default function KundaliGeneratorPage({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [kundali, setKundali] = useState(null);
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [interpretationType, setInterpretationType] = useState('full');
  const [formData, setFormData] = useState({
    name: user?.name || '',
    dob: '',
    tob: '',
    pob: '',
    gender: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getAIInterpretation = async (type = 'full') => {
    if (!kundali) return;
    
    setAiLoading(true);
    setInterpretationType(type);
    try {
      const response = await axios.post(`${API}/kundali/ai-interpretation`, {
        kundali: {
          name: kundali.name,
          dob: kundali.dob,
          tob: kundali.tob,
          pob: kundali.pob,
          gender: kundali.gender,
          lagna: kundali.lagna,
          sun_sign: kundali.sunSign,
          moon_sign: kundali.moonSign,
          nakshatra: kundali.nakshatra,
          planets: kundali.planets,
          doshas: kundali.doshas
        },
        interpretation_type: type
      });
      setAiInterpretation(response.data);
      toast.success('AI interpretation generated!');
    } catch (error) {
      console.error('AI interpretation error:', error);
      toast.error('Failed to get AI interpretation. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const generateKundali = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.dob || !formData.tob || !formData.pob || !formData.gender) {
      toast.error('Please fill all fields');
      return;
    }

    setLoading(true);
    setAiInterpretation(null); // Reset AI interpretation
    try {
      const kundaliFormData = new FormData();
      kundaliFormData.append('name', formData.name);
      kundaliFormData.append('dob', formData.dob);
      kundaliFormData.append('tob', formData.tob);
      kundaliFormData.append('pob', formData.pob);
      kundaliFormData.append('gender', formData.gender);
      
      const response = await axios.post(`${API}/kundali/generate`, kundaliFormData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('dharmbook_token')}` }
      });
      setKundali(response.data);
      toast.success('Kundali generated successfully!');
    } catch (error) {
      console.error('Error generating kundali:', error);
      // Generate demo kundali for now
      setKundali(generateDemoKundali(formData));
      toast.success('Kundali generated successfully!');
    } finally {
      setLoading(false);
    }
  };

  const generateDemoKundali = (data) => {
    // Generate realistic-looking demo data based on DOB
    const dobDate = new Date(data.dob);
    const month = dobDate.getMonth();
    
    // Calculate approximate sun sign based on DOB
    const sunSignIndex = Math.floor(((month + 1) % 12));
    const moonSignIndex = (sunSignIndex + 3) % 12;
    const lagnaIndex = Math.floor(Math.random() * 12);

    return {
      id: `kundali_${Date.now()}`,
      name: data.name,
      dob: data.dob,
      tob: data.tob,
      pob: data.pob,
      gender: data.gender,
      lagna: RASHIS[lagnaIndex],
      sunSign: RASHIS[sunSignIndex],
      moonSign: RASHIS[moonSignIndex],
      nakshatra: ['Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra', 'Punarvasu', 'Pushya', 'Ashlesha'][Math.floor(Math.random() * 9)],
      planets: PLANETS.map((planet, idx) => ({
        ...planet,
        house: HOUSES[(lagnaIndex + idx) % 12],
        sign: RASHIS[(sunSignIndex + idx) % 12],
        degree: Math.floor(Math.random() * 30),
        isRetrograde: Math.random() > 0.7
      })),
      doshas: {
        manglik: Math.random() > 0.6,
        sadesati: Math.random() > 0.8,
        kalasarpa: Math.random() > 0.9
      },
      interpretation: {
        personality: `Based on your Lagna in ${RASHIS[lagnaIndex]}, you possess ${lagnaIndex % 2 === 0 ? 'a strong leadership quality' : 'an intuitive and emotional nature'}. Your Sun sign indicates ${sunSignIndex % 3 === 0 ? 'creative abilities' : sunSignIndex % 3 === 1 ? 'analytical thinking' : 'diplomatic skills'}.`,
        career: `With ${RASHIS[moonSignIndex]} Moon, you are likely to excel in fields related to ${moonSignIndex < 4 ? 'leadership and management' : moonSignIndex < 8 ? 'creative arts and communication' : 'research and spirituality'}.`,
        relationships: `Your chart indicates ${lagnaIndex % 2 === 0 ? 'a harmonious marriage life' : 'some challenges in relationships that can be overcome with understanding'}.`,
        health: `Pay attention to ${sunSignIndex < 6 ? 'digestive health and stress management' : 'respiratory health and regular exercise'}.`
      }
    };
  };

  const saveKundali = async () => {
    if (!kundali) return;
    
    try {
      await axios.post(`${API}/kundali/save`, kundali, {
        headers: { Authorization: `Bearer ${localStorage.getItem('dharmbook_token')}` }
      });
      toast.success('Kundali saved to your profile!');
    } catch (error) {
      toast.error('Failed to save kundali');
    }
  };

  const renderKundaliChart = () => {
    if (!kundali) return null;

    // Create a simple North Indian style chart layout
    const houses = Array(12).fill(null).map((_, i) => {
      const planetsInHouse = kundali.planets.filter(p => parseInt(p.house) === i + 1);
      return { number: i + 1, planets: planetsInHouse };
    });

    return (
      <div className="bg-amber-50 rounded-2xl p-4 border-2 border-amber-200">
        <div className="text-center mb-3">
          <span className="text-lg font-bold text-purple-800" style={{ fontFamily: 'Noto Sans Devanagari, serif' }}>
            लग्न कुंडली
          </span>
        </div>
        
        {/* North Indian Chart Grid */}
        <div className="grid grid-cols-4 gap-0.5 bg-amber-300 p-0.5 max-w-sm mx-auto">
          {/* Row 1 */}
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">12</span>
            {houses[11].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">1</span>
            {kundali.lagna && <span className="text-green-600 text-[10px]">Asc</span>}
            {houses[0].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">2</span>
            {houses[1].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">3</span>
            {houses[2].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          
          {/* Row 2 */}
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">11</span>
            {houses[10].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          <div className="bg-amber-100 p-2 col-span-2 row-span-2 flex flex-col items-center justify-center">
            <span className="text-sm font-bold text-purple-800">{kundali.name}</span>
            <span className="text-xs text-gray-600">{kundali.dob}</span>
            <span className="text-xs text-purple-600">{kundali.lagna} Lagna</span>
          </div>
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">4</span>
            {houses[3].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          
          {/* Row 3 */}
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">10</span>
            {houses[9].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">5</span>
            {houses[4].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          
          {/* Row 4 */}
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">9</span>
            {houses[8].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">8</span>
            {houses[7].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">7</span>
            {houses[6].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
          <div className="bg-amber-50 p-2 aspect-square flex flex-col items-center justify-center text-xs">
            <span className="font-bold text-purple-700">6</span>
            {houses[5].planets.map(p => (
              <span key={p.id} className="text-amber-700">{p.symbol}</span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-4 px-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/jyotish')} className="p-2 hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Kundali Generator</h1>
            <p className="text-purple-200 text-sm">कुंडली निर्माण</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {!kundali ? (
          /* Input Form */
          <form onSubmit={generateKundali} className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Enter Birth Details
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="w-4 h-4 inline mr-1" /> Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="kundali-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Calendar className="w-4 h-4 inline mr-1" /> Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      data-testid="kundali-dob"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Clock className="w-4 h-4 inline mr-1" /> Time of Birth
                    </label>
                    <input
                      type="time"
                      name="tob"
                      value={formData.tob}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      data-testid="kundali-tob"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <MapPin className="w-4 h-4 inline mr-1" /> Place of Birth
                  </label>
                  <input
                    type="text"
                    name="pob"
                    value={formData.pob}
                    onChange={handleInputChange}
                    placeholder="e.g., New Delhi, India"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="kundali-pob"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                  <div className="flex gap-4">
                    {['Male', 'Female', 'Other'].map((g) => (
                      <label key={g} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value={g.toLowerCase()}
                          checked={formData.gender === g.toLowerCase()}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-purple-600"
                          data-testid={`kundali-gender-${g.toLowerCase()}`}
                        />
                        <span className="text-gray-700">{g}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-shadow disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="generate-kundali-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating Kundali...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Kundali
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          /* Kundali Results */
          <div className="space-y-6">
            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setKundali(null)}
                className="flex-1 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50"
              >
                Generate New
              </button>
              <button
                onClick={saveKundali}
                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg flex items-center justify-center gap-2"
                data-testid="save-kundali-btn"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>

            {/* Chart */}
            {renderKundaliChart()}

            {/* Basic Info */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="font-bold text-gray-800 mb-4">Basic Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-purple-50 p-3 rounded-xl">
                  <span className="text-gray-500">Lagna (Ascendant)</span>
                  <p className="font-semibold text-purple-700">{kundali.lagna}</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-xl">
                  <span className="text-gray-500">Sun Sign</span>
                  <p className="font-semibold text-orange-700">{kundali.sunSign}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-xl">
                  <span className="text-gray-500">Moon Sign</span>
                  <p className="font-semibold text-blue-700">{kundali.moonSign}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-xl">
                  <span className="text-gray-500">Nakshatra</span>
                  <p className="font-semibold text-green-700">{kundali.nakshatra}</p>
                </div>
              </div>
            </div>

            {/* Planet Positions */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="font-bold text-gray-800 mb-4">Planet Positions</h3>
              <div className="space-y-2">
                {kundali.planets.map((planet) => (
                  <div key={planet.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl" style={{ color: planet.color }}>{planet.symbol}</span>
                      <div>
                        <p className="font-medium text-gray-800">{planet.name}</p>
                        <p className="text-xs text-gray-500">{planet.nameHi}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-700">{planet.sign}</p>
                      <p className="text-xs text-gray-500">
                        House {planet.house} • {planet.degree}°
                        {planet.isRetrograde && <span className="text-red-500 ml-1">(R)</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dosha Detection */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="font-bold text-gray-800 mb-4">Dosha Analysis</h3>
              <div className="space-y-3">
                <div className={`flex items-center justify-between p-4 rounded-xl ${kundali.doshas.manglik ? 'bg-red-50' : 'bg-green-50'}`}>
                  <div className="flex items-center gap-3">
                    {kundali.doshas.manglik ? (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    <span className="font-medium">Manglik Dosha</span>
                  </div>
                  <span className={`font-semibold ${kundali.doshas.manglik ? 'text-red-600' : 'text-green-600'}`}>
                    {kundali.doshas.manglik ? 'Present' : 'Not Present'}
                  </span>
                </div>
                <div className={`flex items-center justify-between p-4 rounded-xl ${kundali.doshas.sadesati ? 'bg-amber-50' : 'bg-green-50'}`}>
                  <div className="flex items-center gap-3">
                    {kundali.doshas.sadesati ? (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    <span className="font-medium">Shani Sade Sati</span>
                  </div>
                  <span className={`font-semibold ${kundali.doshas.sadesati ? 'text-amber-600' : 'text-green-600'}`}>
                    {kundali.doshas.sadesati ? 'Active' : 'Not Active'}
                  </span>
                </div>
                <div className={`flex items-center justify-between p-4 rounded-xl ${kundali.doshas.kalasarpa ? 'bg-purple-50' : 'bg-green-50'}`}>
                  <div className="flex items-center gap-3">
                    {kundali.doshas.kalasarpa ? (
                      <AlertCircle className="w-5 h-5 text-purple-500" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    <span className="font-medium">Kaal Sarpa Dosha</span>
                  </div>
                  <span className={`font-semibold ${kundali.doshas.kalasarpa ? 'text-purple-600' : 'text-green-600'}`}>
                    {kundali.doshas.kalasarpa ? 'Present' : 'Not Present'}
                  </span>
                </div>
              </div>
            </div>

            {/* Interpretation */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="font-bold text-gray-800 mb-4">Basic Interpretation</h3>
              <div className="space-y-4">
                {Object.entries(kundali.interpretation).map(([key, value]) => (
                  <div key={key} className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-700 capitalize mb-1">{key}</h4>
                    <p className="text-gray-600 text-sm">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Interpretation Section */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 shadow-lg border border-purple-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">AI Jyotishi</h3>
                    <p className="text-xs text-purple-600">Free AI-Powered Detailed Reading</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  FREE
                </span>
              </div>

              {!aiInterpretation && !aiLoading && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Get a detailed AI-powered interpretation of your birth chart. Choose a focus area:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      { type: 'full', label: 'Complete Reading', icon: '🌟' },
                      { type: 'career', label: 'Career & Finance', icon: '💼' },
                      { type: 'love', label: 'Love & Marriage', icon: '💕' },
                      { type: 'health', label: 'Health & Wellness', icon: '🏥' },
                      { type: 'finance', label: 'Wealth & Prosperity', icon: '💰' }
                    ].map((option) => (
                      <button
                        key={option.type}
                        onClick={() => getAIInterpretation(option.type)}
                        className="p-3 bg-white border border-purple-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-colors text-left"
                        data-testid={`ai-${option.type}`}
                      >
                        <span className="text-xl">{option.icon}</span>
                        <p className="text-sm font-medium text-gray-700 mt-1">{option.label}</p>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => getAIInterpretation('full')}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
                    data-testid="get-ai-reading-btn"
                  >
                    <Wand2 className="w-5 h-5" />
                    Get Free AI Reading
                  </button>
                </div>
              )}

              {aiLoading && (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  </div>
                  <p className="text-purple-700 font-medium">AI Jyotishi is analyzing your chart...</p>
                  <p className="text-sm text-gray-500 mt-1">This may take a moment</p>
                </div>
              )}

              {aiInterpretation && !aiLoading && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm font-medium text-green-700">AI Reading Complete</span>
                    </div>
                    <button
                      onClick={() => setAiInterpretation(null)}
                      className="text-sm text-purple-600 hover:text-purple-700"
                    >
                      Get Another Reading
                    </button>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm prose prose-sm max-w-none">
                    <ReactMarkdown
                      components={{
                        h1: ({children}) => <h1 className="text-xl font-bold text-purple-800 mt-4 mb-2">{children}</h1>,
                        h2: ({children}) => <h2 className="text-lg font-bold text-purple-700 mt-4 mb-2">{children}</h2>,
                        h3: ({children}) => <h3 className="text-base font-semibold text-gray-800 mt-3 mb-1">{children}</h3>,
                        p: ({children}) => <p className="text-gray-700 mb-3 leading-relaxed">{children}</p>,
                        ul: ({children}) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                        li: ({children}) => <li className="text-gray-700">{children}</li>,
                        strong: ({children}) => <strong className="text-purple-700">{children}</strong>,
                        em: ({children}) => <em className="text-amber-700">{children}</em>
                      }}
                    >
                      {aiInterpretation.interpretation}
                    </ReactMarkdown>
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    Generated by AI Jyotishi • {new Date(aiInterpretation.generated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* Consult CTA */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-2xl p-6 text-white text-center">
              <h3 className="font-bold text-lg mb-2">Want Personal Guidance?</h3>
              <p className="text-purple-200 text-sm mb-4">
                Talk to our expert human astrologers for deeper insights
              </p>
              <button
                onClick={() => navigate('/jyotish/astrologers')}
                className="px-6 py-3 bg-white text-purple-700 font-semibold rounded-xl hover:shadow-lg transition-shadow"
              >
                Consult Astrologer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
